// Given objects can hold any data type as a value, it makes sense to use any here
/* eslint-disable @typescript-eslint/no-explicit-any */

export type AjaxUtilitiesType = {
  ajax: (settings: {
    data?: Record<string, any>,
    complete?: () => void,
    success?: (response: any) => void,
    error?: (response: any, textStatus?: string, errorThrown?: string) => void,
    url?: string,
    type?: string,
    multiPart?: boolean,
    dataType?: 'xml' | 'html' | 'script' | 'json' | 'jsonp' | 'text' | string | undefined;
  }) => void,
  generateFormData: (data: Record<string, any>) => FormData,
  camelCaseKeys: (object: Record<string, any>) => Record<string, any>,
  snakeCaseKeys: (object: Record<string, any>) => Record<string, any>,
  _caseArray: (
    array: Array<any | Record<string, any>>,
    converter: (object: Record<string, any> | any) => Record<string, any>
  ) => Array<any | Record<string, any>>,
  _isObject: (value: null | Record<string, any> | Record<string, any>[] | any) => boolean,
  _toCamelCase: (string: string) => string,
  _toSnakeCase: (string: string) => string;
}

const AjaxUtilities: AjaxUtilitiesType = {
  ajax: ({
    data, complete, success, error, url, type, multiPart, dataType
  }) => {
    const { camelCaseKeys, snakeCaseKeys, generateFormData } = AjaxUtilities;
    const processedData = multiPart ? generateFormData(data) : snakeCaseKeys(data);

    $.ajax({
      type: type || 'GET',
      url,
      dataType,
      processData: !multiPart,
      contentType: multiPart ? false : 'application/x-www-form-urlencoded; charset=UTF-8',
      data: processedData,
      success: success && ((response) => { success(camelCaseKeys(response)); }),
      error,
      complete
    });
  },

  generateFormData: (data) => {
    const { _toSnakeCase } = AjaxUtilities;
    const formData = new FormData();

    Object.entries(data).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(_toSnakeCase(key), value);
      }
    });

    return formData;
  },

  camelCaseKeys: (object) => {
    if (typeof object === 'string') return object;

    const {
      _toCamelCase,
      camelCaseKeys,
      _isObject,
      _caseArray
    } = AjaxUtilities;

    const camelObject = {};

    Object.entries(object).forEach(([key, value]) => {
      if (_isObject(value)) {
        camelObject[_toCamelCase(key)] = camelCaseKeys(value);
      } else if (Array.isArray(value)) {
        camelObject[_toCamelCase(key)] = _caseArray(value, camelCaseKeys);
      } else {
        camelObject[_toCamelCase(key)] = value;
      }
    });

    return camelObject;
  },

  snakeCaseKeys: (object) => {
    if (!object) { return null; }

    const {
      _toSnakeCase,
      snakeCaseKeys,
      _isObject,
      _caseArray
    } = AjaxUtilities;

    const snakeObject = {};

    Object.entries(object).forEach(([key, value]) => {
      if (_isObject(value)) {
        snakeObject[_toSnakeCase(key)] = snakeCaseKeys(value);
      } else if (Array.isArray(value)) {
        snakeObject[_toSnakeCase(key)] = _caseArray(value, snakeCaseKeys);
      } else {
        snakeObject[_toSnakeCase(key)] = value;
      }
    });

    return snakeObject;
  },

  _caseArray: (array, converter) => {
    const { _isObject, _caseArray } = AjaxUtilities;

    return array.map((value) => {
      if (_isObject(value)) return converter(value);

      if (Array.isArray(value)) return _caseArray(value, converter);

      return value;
    });
  },

  _isObject: (value) => value !== null && typeof value === 'object' && !Array.isArray(value),

  _toCamelCase: (string) => string.replace(/(_\w)/g, (m) => m[1].toUpperCase()),

  _toSnakeCase: (string) => string.replace(/[\w]([A-Z])/g, (m) => `${m[0]}_${m[1]}`).toLowerCase()
};

export default AjaxUtilities;
